// The same fields, fallbacks and blocks as Template::MERGE_FIELDS, POLICY_ROWS,
// BUTTONS and BUTTON_PATHS; MergeFields.test.js fails when they drift.
export const MERGE_FIELDS = [
  { field: 'first_name', label: 'first name', fallback: 'there', sample: 'Jordan' },
  { field: 'agency', label: 'agency', fallback: 'your agency', sample: 'Northstar Insurance' },
  { field: 'broker', label: 'broker', fallback: 'your broker', sample: 'Maya Lewis' },
  { field: 'broker_first_name', label: 'broker first name', fallback: 'your broker', sample: 'Maya' },
  { field: 'policy_line', label: 'policy line', fallback: 'policy', sample: 'auto' },
  { field: 'policy_number', label: 'policy number', fallback: '—', sample: 'HO-••••4821' },
  { field: 'renewal_date', label: 'renewal date', fallback: 'its renewal date', sample: 'October 3, 2026' },
  { field: 'premium', label: 'premium', fallback: '—', sample: '$142.00' },
  { field: 'time_to_renewal', label: 'time to renewal', fallback: 'soon', sample: 'in 30 days' },
];

export const POLICY_ROWS = { Policy: 'policy_number', Renews: 'renewal_date', 'Monthly premium': 'premium' };

export const BUTTONS = {
  coverage_button: { label: 'See my coverage', path: '/hub' },
  ask_broker_button: { label: 'Ask {{broker_first_name}} about this', path: '/hub/messages' },
};

// A block sits on a line of its own; the email draws it as the policy table or
// a button, the portal (and every preview here) as a list or a link.
export const BLOCKS = [
  { block: 'policy_details', label: 'policy details' },
  { block: 'coverage_button', label: 'coverage button' },
  { block: 'ask_broker_button', label: 'ask broker button' },
];

// Template::LEGACY_FIELDS: other names the server still fills, not offered.
export const LEGACY_FIELDS = { policy_type: 'policy_line', broker_name: 'broker' };

const OFFERED_SAMPLE = Object.fromEntries(MERGE_FIELDS.map(({ field, sample }) => [field, sample]));

// Named as samples wherever they are shown, so nobody reads them as a real
// recipient's values.
export const SAMPLE = {
  ...OFFERED_SAMPLE,
  ...Object.fromEntries(Object.entries(LEGACY_FIELDS).map(([legacy, field]) => [legacy, OFFERED_SAMPLE[field]])),
};

export const FALLBACK_NOTE = `If somebody has no value, it reads: ${MERGE_FIELDS.map(
  ({ label, fallback }) => `${label} → “${fallback}”`
).join(' · ')}.`;

// The Send a message box is plain text; the server turns its newlines into
// breaks by this same rule (TemplateRenderer::withLineBreaks).
export const withBreaks = (html) => (/<(p|ul|ol|li)\b/i.test(html) ? html : html.replace(/\r?\n/g, '<br>'));

const BLOCK_ENDS = { p: '\n\n', div: '\n\n', ul: '\n', ol: '\n' };

const textOf = (node) => Array.from(node.childNodes).map((child) => {
  if (child.nodeType === Node.TEXT_NODE) return child.textContent;
  if (child.nodeType !== Node.ELEMENT_NODE) return '';

  const tag = child.tagName.toLowerCase();
  if (tag === 'br') return '\n';
  if (tag === 'li') return `- ${textOf(child).trim()}\n`;

  return textOf(child) + (BLOCK_ENDS[tag] ?? '');
}).join('');

// A stored body going back into that box. TemplateRenderer::renderChat's rule,
// except that paragraphs keep a blank line between them.
export const toPlainText = (html) =>
  textOf(new DOMParser().parseFromString(html ?? '', 'text/html').body)
    .replace(/[ \t]*\n[ \t]*/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const BLOCK_NAMES = BLOCKS.map(({ block }) => block).join('|');
const FIELD = `\\{\\{\\s*(${Object.keys(SAMPLE).join('|')})\\s*\\}\\}`;
const TOKEN = new RegExp(`<p>\\s*\\{\\{(${BLOCK_NAMES})\\}\\}\\s*</p>|\\{\\{(${BLOCK_NAMES})\\}\\}(?:<br>)?|${FIELD}`, 'g');

const fill = (text) => text.replace(new RegExp(FIELD, 'g'), (match, field) => SAMPLE[field]);

const sampleBlock = (block) => {
  if (block === 'policy_details') {
    return `<ul>${Object.entries(POLICY_ROWS).map(([label, field]) => `<li>${label}: <b>${SAMPLE[field]}</b></li>`).join('')}</ul>`;
  }
  return `<p><a href="${BUTTONS[block].path}">${fill(BUTTONS[block].label)}</a></p>`;
};

export const withSamples = (text) =>
  withBreaks(text ?? '').replace(TOKEN, (match, wrapped, bare, field) => (field ? SAMPLE[field] : sampleBlock(wrapped || bare)));

const PERSONAL = new RegExp(`${FIELD}|\\{\\{(${BLOCK_NAMES})\\}\\}`);

export const usesMergeFields = (text) => PERSONAL.test(text ?? '');
